import { Box, makeStyles, Paper, Tab, Tabs, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';

import { BenchmarkView } from '../../../../generated/model/benchmarkView';
import { useCompaniesDispatch } from '../../shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../shared/CompanyContext/CompanyContext.types';
import CompanyBranches from './CompanyBranches';
import CompanyDocuments from './CompanyDocuments';
import CompanyFinancials from './CompanyFinancials';
import CompanyInfo from './CompanyInfo/CompanyInfo';
import CompanyIssues from './CompanyIssues';
import CompanyOwnership from './CompanyOwnership';
import CompanyRatios from './CompanyRatios';
import CompanyFinancialsUnprocessed from './CompanyRawFinancials';
import OutlinedLabel from './shared/OutlinedChipLabel/OutlinedLabel';

export interface TabItem {
  name: string;
  disabled?: boolean;
  component: React.ReactNode;
}

export const TabNamesEnum = {
  COMPANY: 'company',
  OWNERSHIP: 'ownership',
  FINANCIALS: 'financials',
  RAW_FINANCIALS: 'As-Reported Financials',
  RATIOS: 'ratios',
  BRANCHES: 'branches',
  DOCUMENTS: 'documents',
  POTENTIAL_ISSUES: 'ESG Compliance',
};

export interface Props {
  tabsOffset?: number;
  consolidationPreference?: BenchmarkView.ConsolidationPreferenceEnum;
}

const CompanyTabs: React.FC<Props> = ({ tabsOffset, consolidationPreference }) => {
  const tabItems: TabItem[] = [
    { name: TabNamesEnum.COMPANY, component: <CompanyInfo /> },
    { name: TabNamesEnum.OWNERSHIP, component: <CompanyOwnership /> },
    {
      name: TabNamesEnum.FINANCIALS,
      component: <CompanyFinancials consolidationPreference={consolidationPreference} />,
    },
    {
      name: TabNamesEnum.RAW_FINANCIALS,
      component: <CompanyFinancialsUnprocessed />,
      disabled: !!(window as any)?.env?.DISABLE_RAW_FINANCIALS,
    },
    {
      name: TabNamesEnum.RATIOS,
      component: <CompanyRatios consolidationPreference={consolidationPreference} />,
    },
    { name: TabNamesEnum.BRANCHES, component: <CompanyBranches /> },
    { name: TabNamesEnum.DOCUMENTS, component: <CompanyDocuments /> },
    { name: TabNamesEnum.POTENTIAL_ISSUES, component: <CompanyIssues />, disabled: true },
  ];

  const classes = useStyles({ tabsOffset });
  const [activeTab, setActiveTab] = useState(tabItems[0].name);
  const dispatchCompany = useCompaniesDispatch();

  useEffect(() => {
    dispatchCompany({
      type: CompanyContextActionTypes.SetClickedCompanyTabName,
      payload: activeTab,
    });
  }, [activeTab, dispatchCompany]);

  const handleChange = (event: React.ChangeEvent<{}>, tab: string) => {
    ReactGA.event({ category: 'COMPANY', action: 'TAB_CLICK', label: tab });
    setActiveTab(tab);
  };

  const disabledTabWithLabel = (tab: TabItem) => {
    return (
      tab.disabled && (
        <Box key={'disabledTab'} className={classes.labelBox}>
          <Tab
            id={`full-width-tab-${tab.name}`}
            key={tab.name}
            value={tab.name}
            label={tab.name}
            disabled={tab.disabled}
          />
          <OutlinedLabel label={'app:comingSoon'} />
        </Box>
      )
    );
  };

  const enabledTab = (tab: TabItem) => {
    return (
      !tab.disabled && (
        <Tab
          id={`full-width-tab-${tab.name}`}
          key={tab.name}
          value={tab.name}
          label={tab.name}
          disabled={tab.disabled}
        />
      )
    );
  };

  return (
    <Box className={classes.tabsContainer}>
      <Paper square className={classes.tabItems}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          centered
          indicatorColor="primary"
          textColor="primary"
        >
          {tabItems.map((tab: TabItem) => {
            return [enabledTab(tab), disabledTabWithLabel(tab)];
          })}
        </Tabs>
      </Paper>
      <Box className={classes.tabsBox}>
        {tabItems.map((tab: TabItem) => {
          return (
            activeTab === tab.name && (
              <Box role="tabpanel" id={`full-width-tabpanel-${tab.name}`} key={tab.name}>
                {tab.component}
              </Box>
            )
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<Theme, Props>((theme) => ({
  tabsBox: {
    overflowY: 'auto',
    height: ({ tabsOffset }) =>
      tabsOffset ? `calc(100vh - ${tabsOffset}px)` : 'calc(100vh - 80px)',
  },
  tabItems: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(8),
  },
  tabsContainer: {
    position: 'relative',
    '& .Mui-selected': {
      '& .MuiTab-wrapper': {
        color: theme.palette.primary.main,
      },
    },
    '& .Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .MuiTab-wrapper': {
      textTransform: 'capitalize',
      color: theme.palette.secondary.dark,
    },
    '& .MuiTab-root': {
      [theme.breakpoints.down('md')]: {
        minWidth: '100px',
      },
    },
  },
  labelBox: {
    display: 'inline',
  },
}));

export default CompanyTabs;
